import {DialogContent, Stack, Typography} from '@mui/material';
import {IconBadge, useAlertDialog, VisibilityLoading} from 'components';
import React from 'react';
import {useShouldUserDoExtendedBankIdStep, useTranslate} from 'hooks';
import {useCard} from '@resurs-internal/rb-app-api';
import {BaseDialog} from '@resurs-internal/mui';
import {useActivateCardDialog} from '../../../hooks/useDialog/useActivateCardDialog';

export const ActivateCardDialog: React.FC = () => {
  const dialog = useActivateCardDialog();

  if (!dialog.open || !dialog.state) return null;

  return (
    <ActivateCardDialogView cardId={dialog.state.id} onClose={dialog.onClose} />
  );
};

const ActivateCardDialogView: React.FC<{
  cardId: string | undefined;
  onClose: () => void;
}> = ({cardId, onClose}) => {
  const translate = useTranslate();
  const alert = useAlertDialog();
  const card = useCard(cardId);
  const shouldDoExtendedBankIdStep = useShouldUserDoExtendedBankIdStep();

  const onSubmit = async () => {
    try {
      await card.activate.mutateAsync();
      onClose();
      alert.success({
        title: translate('dialogs.activate-card.success.title'),
        subtitle: translate('dialogs.activate-card.success.subtitle'),
        actions: [
          {
            title: translate('common.ok'),
            onClick: alert.onClose,
          },
        ],
      });
    } catch (error) {
      onClose();
      alert.error({
        title: translate('common.error'),
        subtitle: translate('common.error.message'),
      });
    }
  };

  if (shouldDoExtendedBankIdStep) {
    return (
      <BaseDialog
        header={translate('dialogs.activate-card.header')}
        maxWidth="sm"
        open
        onClose={onClose}
        actions={[
          {
            title: translate('common.cancel'),
            onClick: onClose,
          },
        ]}>
        <DialogContent>
          <Stack spacing={2} alignItems={'center'}>
            <IconBadge type={'font'} icon={'shield-halved'} size="xl" />
            <Typography variant={'h4'} component={'h3'}>
              {translate('dialogs.activate-card.extended-bankid.title')}
            </Typography>
            <Typography variant={'body1'} whiteSpace={'break-spaces'}>
              {translate('dialogs.activate-card.extended-bankid.subtitle')}
            </Typography>
            <Typography>
              {translate(
                'dialogs.activate-card.extended-bankid.alternative-route',
              )}
            </Typography>
          </Stack>
        </DialogContent>
      </BaseDialog>
    );
  }

  return (
    <BaseDialog
      header={translate('dialogs.activate-card.header')}
      maxWidth="sm"
      open
      onClose={onClose}
      actions={[
        {
          title: translate('common.cancel'),
          onClick: onClose,
        },
        {
          title: translate('common.confirm'),
          disabled: card.activate.isPending,
          onClick: onSubmit,
        },
      ]}>
      <DialogContent>
        <Stack spacing={2} alignItems={'center'}>
          <VisibilityLoading isLoading={card.activate.isPending}>
            <IconBadge type={'font'} icon={'credit-card'} size="xl" />
            <Typography variant={'h4'} component={'h3'}>
              {translate('dialogs.activate-card.title')}
            </Typography>
            <Typography variant={'body1'} whiteSpace={'break-spaces'}>
              {translate('dialogs.activate-card.subtitle')}
            </Typography>
          </VisibilityLoading>
        </Stack>
      </DialogContent>
    </BaseDialog>
  );
};
